import { Box, HStack, useBreakpointValue } from '@chakra-ui/react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { ScheduleCalendarDayNavigation } from '@/components/ui/features/schedule/ScheduleCalendar/components/ScheduleCalendarDayNavigation';
import { ScheduleCalendarWeekNavigation } from '@/components/ui/features/schedule/ScheduleCalendar/components/ScheduleCalendarWeekNavigation';
import { ViewType } from '@/components/ui/features/schedule/static';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';

export interface IScheduleCalendarHeaderProps {
  days: Array<string>;
  timeZone: string;
  isDesktop: boolean;
  endEventDay: string;
  startEventDay: string;
}

export interface IUrlQueryStringParams {
  day?: string;
  view?: string;
  view_type: ViewType;
}

export const ScheduleCalendarHeader: FC<IScheduleCalendarHeaderProps> = ({
  days,
  timeZone,
  isDesktop,
  endEventDay,
  startEventDay,
}) => {
  const { view = 'day', view_type, change } = useQueryParams<IUrlQueryStringParams>({
    day: StringParam,
    view: StringParam,
    view_type: StringParam,
  });

  const isMobile = useBreakpointValue({ base: true, md: false });

  const { t } = useTranslation();

  const handleOnChangeView = (calendarView: string) => {
    change(
      { view: calendarView.replace('timeGrid', '').toLowerCase(), day: null, view_type },
      { method: 'replace', shallow: true }
    );
  };

  const isListView = view_type === ViewType.LIST;

  if (isListView || isMobile) {
    return <ScheduleCalendarDayNavigation days={days} timeZone={timeZone} isDesktop={isDesktop} />;
  }

  return (
    <Box w="full">
      <Box>
        {view === 'day' && <ScheduleCalendarDayNavigation days={days} timeZone={timeZone} isDesktop={isDesktop} />}
        {view === 'week' && (
          <ScheduleCalendarWeekNavigation timeZone={timeZone} endEventDay={endEventDay} startEventDay={startEventDay} />
        )}
      </Box>
      <HStack alignItems={'baseline'}>
        <Box
          aria-selected={view === 'day'}
          fontWeight="bold"
          fontSize="xl"
          cursor="pointer"
          onClick={() => handleOnChangeView('timeGridDay')}
          _selected={{
            color: 'violet.500',
            borderBottom: '3px solid',
            borderColor: 'violet.500',
          }}
        >
          {t('common.day')}
        </Box>
        <Box
          aria-selected={view === 'week'}
          fontWeight="bold"
          fontSize="xl"
          cursor="pointer"
          onClick={() => handleOnChangeView('timeGridWeek')}
          _selected={{
            color: 'violet.500',
            borderBottom: '3px solid',
            borderColor: 'violet.500',
          }}
        >
          {t('common.week')}
        </Box>
      </HStack>
    </Box>
  );
};
